import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { Seo, breadcrumb } from "../lib/seo";

export default function ContactDistributore() {
  const [form, setForm] = useState({ nome: "", email: "", messaggio: "", website: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.nome.trim().length < 2) return "Inserisci il tuo nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Inserisci un indirizzo email valido.";
    if (form.messaggio.trim().length < 5) return "Scrivi un messaggio (almeno 5 caratteri).";
    if (!privacy) return "Devi accettare l'informativa privacy.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, accept_privacy: privacy });
      setSent(true);
      toast.success("Richiesta inviata");
    } catch (er) {
      toast.error(apiError(er.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const crumb = breadcrumb([{ name: "Home", path: "/" }, { name: "Contatta il distributore", path: "/contatta-il-distributore" }]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-14" data-testid="contact-page">
      <Seo
        title="Contatta il distributore | Herbalife Rossano"
        description="Hai domande sui prodotti Herbalife, sugli ordini o sulle modalità di acquisto? Invia una richiesta al distributore indipendente tramite il modulo di contatto."
        canonical="/contatta-il-distributore"
        jsonLd={crumb}
      />
      <h1 className="text-3xl sm:text-4xl font-extrabold font-display text-slate-900 flex items-center gap-3"><MessageCircle className="w-8 h-8 text-emerald-600" /> Contatta il distributore</h1>
      <p className="mt-4 text-slate-600 leading-relaxed">Per informazioni sui prodotti, sugli ordini o sulle modalità di acquisto, puoi inviare una richiesta tramite il modulo. Ti risponderemo utilizzando l'indirizzo email indicato nel modulo.</p>

      {sent ? (
        <Card className="mt-8 p-8 rounded-2xl border-emerald-200 bg-emerald-50/60 text-center" data-testid="contact-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h2 className="mt-4 text-xl font-bold font-display text-slate-900">Richiesta inviata</h2>
          <p className="mt-2 text-slate-600">Grazie per averci contattato. Riceverai una risposta all'indirizzo email che hai indicato.</p>
          <Link to="/catalogo"><Button className="mt-6 rounded-full" data-testid="contact-back-catalog">Torna al catalogo</Button></Link>
        </Card>
      ) : (
        <Card className="mt-8 p-6 rounded-2xl border-slate-200">
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <Label className="mb-1.5 block">Nome *</Label>
              <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="contact-nome" />
            </div>
            <div>
              <Label className="mb-1.5 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" />
            </div>
            <div>
              <Label className="mb-1.5 block">Messaggio *</Label>
              <Textarea rows={5} value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} data-testid="contact-messaggio" />
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5" data-testid="contact-privacy" />
              <span>Ho letto e accetto l'<Link to="/privacy" className="text-primary underline">informativa privacy</Link> *</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full rounded-full py-6" data-testid="contact-submit"><Send className="w-4 h-4 mr-2" /> {loading ? "Invio in corso..." : "Invia richiesta"}</Button>
          </form>
        </Card>
      )}
    </div>
  );
}
